import { useId, type ReactNode } from "react";

import {
  variablePatternAliases,
  rewritePatternTokens,
} from "../../lib/variablePatterns";
import { useVariableDeclarations } from "../../lib/useVariableDeclarations";
import { useVariableScopeLabels } from "../../lib/useVariableScopeLabels";
import { docUrl } from "../../lib/docLinks";
import {
  HelpTip,
  Inline,
  RouterLink,
  Stack,
  Textarea,
  TextInput,
} from "../../ui";
import { InsertVariableMenu, insertVariableToken } from "./InsertVariableMenu";

export interface VariablePatternFieldProps {
  agentId: string;
  disabled: boolean;
  help: ReactNode;
  label: string;
  multiline?: boolean;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}

export function VariablePatternField({
  agentId,
  disabled,
  help,
  label,
  multiline = false,
  onChange,
  placeholder,
  value,
}: VariablePatternFieldProps) {
  const id = useId();
  const declarations = useVariableDeclarations(agentId);
  const variables = declarations.isError ? [] : (declarations.data ?? []);
  const scopes = useVariableScopeLabels(agentId, variables);
  const aliases = variablePatternAliases(variables);
  const displayed = rewritePatternTokens(value, aliases.display);
  const Field = multiline ? Textarea : TextInput;
  function update(displayValue: string) {
    onChange(rewritePatternTokens(displayValue, aliases.stored, true));
  }
  return (
    <Stack gap={2}>
      <Field
        disabled={disabled}
        help={help}
        id={id}
        label={label}
        placeholder={placeholder}
        required
        value={displayed}
        {...(multiline ? { rows: 4 } : {})}
        onChange={(event) => {
          update(event.currentTarget.value);
        }}
      />
      <Inline gap={2}>
        <InsertVariableMenu
          disabled={disabled || !agentId}
          emptyAction={
            <RouterLink
              params={{ agentId }}
              rel="noopener"
              search={{ new: 1 }}
              target="_blank"
              to="/agents/$agentId/variables"
            >
              Declare variable
            </RouterLink>
          }
          error={declarations.isError}
          isPending={declarations.isPending || declarations.isFetching}
          onRetry={() => {
            void declarations.refetch();
          }}
          description={
            scopes.isPending
              ? "Loading names. IDs distinguish declarations."
              : scopes.isError
                ? "Names unavailable. IDs distinguish declarations."
                : undefined
          }
          variables={variables.map((variable) => ({
            ...variable,
            scopeLabel: scopes.labels.get(variable.id),
          }))}
          onInsert={(variable) => {
            const element = document.getElementById(id) as
              | HTMLInputElement
              | HTMLTextAreaElement
              | null;
            const inserted = insertVariableToken(
              displayed,
              aliases.display.get(variable.id) ?? variable.id,
              element?.selectionStart ?? displayed.length,
              element?.selectionEnd ?? displayed.length,
            );
            update(inserted.value);
            window.requestAnimationFrame(() => {
              element?.focus();
              element?.setSelectionRange(inserted.cursor, inserted.cursor);
            });
          }}
        />
        <HelpTip
          docHref={docUrl("guides/use-variables-in-simulations")}
          docLabel="Use variables in simulations"
          label="About Marker variables"
        >
          {
            "Insert a declared variable to check each simulated call against its frozen inputs. Markers with variables run on simulated Transcripts only. Each call must supply the referenced value."
          }
        </HelpTip>
      </Inline>
    </Stack>
  );
}
